import React, { useEffect, useReducer, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
} from "@mui/material";
import TableHeader from "../table/TableHeader";
import useDebounce from "../../hooks/useDebounce";
import MainTableBody from "../table/MainTableBody";
import CustomTable from "../table/CustomTable";
import { LoadingTableSkeleton } from "../../utils/common";
import NoDataTable from "../table/NoDataTable";
import { Add, CloseOutlined } from "@mui/icons-material";
import { CustomizeTextField } from "../templates/TemplateTable";
import VariableTableRow from "./VariableTableRow";
import useAuthFetch from "../../hooks/useAuthFetch";
import ConfirmBox from "../ConfirmBox";
import toast from "react-hot-toast";


const actions = Object.freeze({
  pageChange: "PAGE_CHANGE",
  perPageChange: "PER_PAGE_CHANGE",
  queryChange: "QUERY_CHANGE",
  filterChange: "FILTER_CHANGE",
});

export const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  paddingLeft: 10,
  paddingRight: 10,
  paddingTop: 8,
  paddingBottom: 8,
};

export const CreateVariableModal = ({ refetch, setOpenErr, setErrMsg }) => {
  const [variableName, setVariableName] = React.useState("");
  const [productName, setProductName] = React.useState("");
  const [productType, setProductType] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const customFetch = useAuthFetch();
  const [validationErr, setValidationErr] = useState({
    variableName: false,
    productName: false,
    productType: false,
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setVariableName("")
    setProductName("")
    setProductType("")
    setValidationErr({
      variableName: false,
      productName: false,
      productType: false,
    })
    setOpen(false);

  }

  const validateFields = () => {
    const errors = {
      variableName: !/^[A-Za-z+\s]{1,60}$/.test(variableName),
      productName: !/^[A-Za-z0-9\s]{1,30}$/.test(productName),
      productType: productType === "",
    };

    setValidationErr(errors);
    return !Object.values(errors).some((err) => err);
  };


  const handleSubmit = async () => {
    if (!validateFields()) return;
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/variableMappings/create`
    );
    const bodyContent = { productType, variableName, productName }
    const method = "POST"
    const { response, status, message } = await customFetch(
      url,
      method,
      bodyContent
    );

    if (status === 201) {
      toast.success("Variable Mapping Created Succesfully!", {
        position: "bottom-right",
      })
      refetch()

    } else {
      setOpenErr(true)
      setErrMsg(message ? message : "Something went wrong while creating variable mapping!")
    }
    handleClose();
  }

  return (
    <div className="">
      <button
        onClick={handleOpen}
        className=" bg-purple-300 flex items-center py-2 pl-2 pr-3 text-white font-bold rounded"
      >
        <Add fontSize="small" className="ml-0 pl-0 " />
        Create
      </button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style} className="rounded-xl relative">
          <button className="absolute top-2 right-3" onClick={handleClose}>
            <CloseOutlined className="w-5 h-5  text-gray-450 hover:text-black" />
          </button>
          <p className="text-2xl mb-0 font-extrabold text-center text-gray-850">
            Create New Variable Mapping
          </p>
          {[
            { label: "Enter Variable Name", value: variableName, setter: setVariableName, error: validationErr.variableName, errorMsg: "Variable Name must be alphabets, spaces, or '+' (1-60 chars)." },
            { label: "Enter Product Name", value: productName, setter: setProductName, error: validationErr.productName, errorMsg: "Product Name must be alphanumeric (1-30 chars)." },
          ].map(({ label, value, setter, error, errorMsg }, index) => (
            <div key={index} className="mt-4">
              <FormControl variant="outlined" className="w-full">
                <CustomizeTextField
                  placeholder={label}
                  className="bg-[#F3F4F8] text-black w-full rounded-lg"
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                />
              </FormControl>
              {error && <p className="text-red-500 text-sm mt-1">{errorMsg}</p>}
            </div>
          ))}

          <div className="mt-6 ml-1">
            <Box className="w-full">
              <label className="block text-black-150 mb-2">Product Type</label>
              <FormControl fullWidth>
                <RadioGroup
                  row
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  className=""
                  name="controlled-radio-buttons-group"
                  value={productType}
                  onChange={(e) => setProductType(e.target.value)}
                >
                  <FormControlLabel
                    value="COMMON"
                    control={<Radio />}
                    label="Common"
                  />
                  <FormControlLabel
                    value="SINGLE"
                    control={<Radio />}
                    label="Single"
                  />
                  <FormControlLabel
                    value="MULTIPLE"
                    control={<Radio />}
                    label="Multiple"
                  />
                </RadioGroup>
              </FormControl>
              {validationErr.productType && <p className="text-red-500 text-sm mt-1">Product Type is required.</p>}
            </Box>
          </div>

          <div className="mt-7 flex items-center justify-center">
            <button
              className="bg-purple-300 py-3 w-full text-white text-base font-bold rounded-lg"
              onClick={handleSubmit}
            >
              Create
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

const EditVariableModal = ({ setErrMsg, open, setOpen, row, refetch, setOpenErr }) => {
  const customFetch = useAuthFetch();
  const [variableName, setVariableName] = React.useState(row?.variableName);
  const [productName, setProductName] = React.useState(row?.productName);
  const [productType, setProductType] = React.useState(row?.productType);
  const [validationErr, setValidationErr] = useState({
    variableName: false,
    productName: false,
    productType: false,
  });

  const handleSubmit = async () => {
    if (!validateFields()) return;
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/variableMappings/update`
    );

    const bodyContent = { mappingId: row?.mappingId, productType, variableName, productName }
    const method = "POST"
    const { response, status, message } = await customFetch(
      url,
      method,
      bodyContent
    );


    if (status === 200) {
      toast.success("Variable Mapping Updated Succesfully!", {
        position: "bottom-right",
      })
      refetch()
    }
    else {
      setOpenErr(true)
      setErrMsg(message ? message : "Something went wrong while updating variable mapping!")
    }
    handleClose();
  }

  const handleClose = () => {
    setVariableName("")
    setProductName("")
    setProductType("")
    setValidationErr({
      variableName: false,
      productName: false,
      productType: false,
    })
    setOpen(false);
  }


  const validateFields = () => {
    const errors = {
      variableName: !/^[A-Za-z+\s]{1,60}$/.test(variableName),
      productName: !/^[A-Za-z0-9\s]{1,30}$/.test(productName),
      productType: productType === "",
    };

    setValidationErr(errors);
    return !Object.values(errors).some((err) => err);
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 8,
            paddingBottom: 8,
          }}
          className="rounded-xl relative"
        >
          <button className="absolute top-2 right-3" onClick={handleClose}>
            <CloseOutlined className="w-5 h-5  text-gray-450 hover:text-black" />
          </button>
          <p className="text-2xl mb-0 font-extrabold text-center text-gray-850">
            Edit Variable Details
          </p>

          {[
            { label: "Enter Variable Name", value: variableName, setter: setVariableName, error: validationErr.variableName, errorMsg: "Variable Name must be alphabets, spaces, or '+' (1-60 chars)." },
            { label: "Enter Product Name", value: productName, setter: setProductName, error: validationErr.productName, errorMsg: "Product Name must be alphanumeric (1-30 chars)." },
          ].map(({ label, value, setter, error, errorMsg }, index) => (
            <div key={index} className="mt-4">
              <FormControl variant="outlined" className="w-full">
                <CustomizeTextField
                  placeholder={label}
                  className="bg-[#F3F4F8] text-black w-full rounded-lg"
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                />
              </FormControl>
              {error && <p className="text-red-500 text-sm mt-1">{errorMsg}</p>}
            </div>
          ))}


          <div className="mt-6 ml-1">
            <Box className="w-full">
              <label className="block text-black-150 mb-2">Product Type</label>
              <FormControl fullWidth>
                <RadioGroup
                  row
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  className=""
                  name="controlled-radio-buttons-group"
                  value={productType}
                  onChange={(e) => setProductType(e.target.value)}
                >
                  <FormControlLabel
                    value="COMMON"
                    control={<Radio />}
                    label="Common"
                  />
                  <FormControlLabel
                    value="SINGLE"
                    control={<Radio />}
                    label="Single"
                  />
                  <FormControlLabel
                    value="MULTIPLE"
                    control={<Radio />}
                    label="Multiple"
                  />
                </RadioGroup>
              </FormControl>
              {validationErr.productType && <p className="text-red-500 text-sm mt-1">Product Type is required.</p>}
            </Box>
          </div>

          <div className="mt-7 flex items-center justify-center">
            <button
              className="bg-purple-300 py-3 px-9 text-white text-base font-bold rounded-lg"
              onClick={handleSubmit}
            >
              Update
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default function VariableTable({ reqState, handleFetch, handleChange, productType }) {
  const [afterFirstLoad, setAfterFirstLoad] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState("");
  const calcHeight = 165
  const [openErr, setOpenErr] = useState(false)
  const [errMsg, setErrMsg] = useState(null)

  const [query, setQuery] = useState({
    page: 1,
    perPage: 20,
    query: "",
    userId: "",
    meetingId: "",
  });

  function reducer(state, action) {
    switch (action.type) {
      case actions.pageChange:
        return { ...state, page: action.payload };

      case actions.perPageChange:
        return { ...state, perPage: action.payload, page: 1 };

      case actions.queryChange:
        if (state.query === action.payload) return state;
        return { ...state, query: action.payload, page: 1 };

      case actions.filterChange:
        return { ...state, userId: action.payload, page: 1 };

      default:
        throw new Error("No such reducer action");
    }
  }

  const [state, dispatch] = useReducer(reducer, query);
  const { isLoading, response } = reqState;
  const [keyword, searchTerm, setSearchTerm] = useDebounce(state.query, 500);

  useEffect(() => {
    dispatch({ type: actions.queryChange, payload: keyword });
  }, [keyword]);

  useEffect(() => {
    if (afterFirstLoad) {
      const q = { ...state };
      if (!q.query) delete q.query;
      if (!q.meetingId) delete q.meetingId;
      if (!q.userId) delete q.userId;
      setQuery(q);
    }
  }, [state]);

  const refetch = () => {
    // const q = { ...query, page: 1 };
    // if (!q.query) delete q.query;
    // if (!q.userId) delete q.userId;
    // setQuery(q);
    handleFetch()
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAfterFirstLoad(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const columns = [
    "Mapping Id",
    "Variable Name",
    "Product Name",
    "Product Type",
    "Actions",
  ];

  return (
    <>
      <div
        className="py-1 px-4"
        style={{
          height: `calc(100vh - 100px)`,
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="mt-0 mb-0 flex items-center">
            <label className=" block text-black-150">Product Type</label>
            <RadioGroup
              row
              aria-labelledby="demo-controlled-radio-buttons-group"
              className="ml-6"
              name="controlled-radio-buttons-group"
              value={productType}
              onChange={handleChange}
            >
              <FormControlLabel
                value="SINGLE"
                control={<Radio />}
                label="Single"
              />
              <FormControlLabel
                value="MULTIPLE"
                control={<Radio />}
                label="Multiple"
              />
            </RadioGroup>
          </div>
          <div className="flex flex-row">
            <button onClick={() => refetch()} className="border focus:border-blue-600 flex items-center border-[#E0E0E0] mx-2 rounded py-1.5 px-3">Refresh Data</button>
            <CreateVariableModal setErrMsg={setErrMsg} setOpenErr={setOpenErr} refetch={refetch} />
          </div>


        </div>
        {!response && !isLoading ? (
          <NoDataTable
            columns={columns}
            recordingKeyClassName={true}
            message={"Server Error, Please try again later."}
          />
        ) : response?.data?.length === 0 && !isLoading ? (
          <NoDataTable
            columns={columns}
            recordingKeyClassName={true}
            message={"No varible mappings available."}
          />
        ) : (
          <CustomTable calcHeight={calcHeight}>
            <TableHeader columns={columns} />
            <MainTableBody isLoading={isLoading}>
              {response?.length === 0
                ? [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <LoadingTableSkeleton key={i} index={i} />
                ))
                : response?.map((row, i) => (
                  <VariableTableRow
                    key={i}
                    row={row}
                    refetch={refetch}
                    setOpen={setOpen}
                    setSelectedRow={setSelectedRow}
                  />
                ))}
            </MainTableBody>

            {/* {response?.pageInfo && (
              <TablePagination
                className="sticky bottom-0 bg-white"
                rowsPerPageOptions={[5, 10, 25]}
                
                count={response?.pageInfo.total}
                rowsPerPage={response?.pageInfo.perPage}
                page={response?.pageInfo?.currentPage}
                onPageChange={(activePage) => {
                  if (!isLoading) {
                    dispatch({
                      type: actions.pageChange,
                      payload: activePage,
                    });
                  }
                }}
              />
            )} */}
          </CustomTable>
        )}
      </div>
      {open && (
        <EditVariableModal setErrMsg={setErrMsg} setOpenErr={setOpenErr} refetch={refetch} open={open} setOpen={setOpen} row={selectedRow} />

      )}
      <ConfirmBox
        open={openErr}
        setOpen={setOpenErr}
        buttonText={"Close"}
        text={errMsg}
      />
    </>
  );
}
